import { useId } from 'react'
import type { ReportMetric, ReportSection as Section } from '../../api/types'
import { MetricCard } from './MetricCard'
import { MetricEvidence } from './MetricEvidence'
import { toneFor } from './tone'
import styles from './Reports.module.css'

/**
 * One section, answering one management question.
 *
 * The metric cards are the section. The evidence panel opens beneath the whole
 * row rather than inside a card, so the grid never reflows when something is
 * expanded and the reader's eye keeps its place.
 *
 * Which metric is open is owned by the page, not by this component: only one
 * may be open across all three sections, so opening a card here has to close
 * one over there.
 */
export function ReportSection({
  section,
  openKey,
  onToggle,
}: {
  section: Section
  openKey: string | null
  onToggle: (key: string) => void
}) {
  const baseId = useId()
  const tone = toneFor(section.key)
  const open = section.metrics.find((m) => m.key === openKey) ?? null

  // data-tone sits on the section so --accent cascades to everything inside it —
  // badge, number, cards and panel. Setting it on the badge alone left the
  // section number inheriting the neutral title colour.
  return (
    <section className={styles.section} data-tone={tone} aria-labelledby={`${baseId}-title`}>
      <header className={styles.sectionHead}>
        <span className={styles.sectionBar} aria-hidden="true" />
        <div>
          <h2 id={`${baseId}-title`} className={styles.sectionTitle}>
            {section.title}
          </h2>
          <p className={styles.sectionQuestion}>{section.question}</p>
        </div>
      </header>

      {section.metrics.length === 0 ? (
        <p className={styles.empty}>{section.empty_message}</p>
      ) : (
        <>
          <ul className={styles.metrics}>
            {section.metrics.map((metric: ReportMetric) => (
              <MetricCard
                key={metric.key}
                metric={metric}
                tone={tone}
                expanded={metric.key === openKey}
                onToggle={() => onToggle(metric.key)}
                id={`${baseId}-${metric.key}`}
                controls={`${baseId}-panel`}
              />
            ))}
          </ul>

          {open ? (
            <MetricEvidence
              key={open.key}
              metric={open}
              tone={tone}
              id={`${baseId}-panel`}
              labelledBy={`${baseId}-${open.key}`}
            />
          ) : null}
        </>
      )}
    </section>
  )
}
